/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useMutation } from '@apollo/client';
import type {
  LearnerCalendars_ToggleShouldDisplayContentInput as ToggleShouldDisplayContentInput,
  LearnerCalendars_ToggleShouldDisplayContentResult as ToggleShouldDisplayContentResult,
} from '__generated__/graphql-types';
import { compose } from 'recompose';

import user from 'js/lib/user';

import { SelectField, SelectOption } from '@coursera/cds-core';

import withCalendarData from 'bundles/calendar-sync/withCalendarData';
import type { CalendarOptionValuesType } from 'bundles/degree-home/components/v2/Calendar/constants/constants';
import { calendarOptions } from 'bundles/degree-home/components/v2/Calendar/constants/constants';
import ToggleCalendarMutation from 'bundles/degree-home/components/v2/Calendar/data/ToggleCalendarMutuation.graphql';

import _t from 'i18n!nls/degree-home';

type CalendarType = { google: string; webcal: string };

type Props = {
  calendar?: CalendarType;
};

const getCalendarOptions = (value: CalendarOptionValuesType, calendar: CalendarType) => {
  const { google, webcal } = calendar;

  switch (value) {
    case 'google':
      return {
        label: _t('Google Calendar'),
        link: google,
        trackingName: 'download_cal_opt_google',
      };
    case 'webcal':
      return {
        label: _t('Apple Calendar'),
        link: webcal,
        trackingName: 'download_cal_opt_apple',
      };
    default:
      return {
        label: '',
        link: '',
        trackingName: '',
      };
  }
};

export const CalendarSyncDropdown: React.VFC<Props> = ({ calendar }) => {
  const [toggleCalendarEnabled] = useMutation<
    ToggleShouldDisplayContentResult,
    { body: ToggleShouldDisplayContentInput }
  >(ToggleCalendarMutation, {
    context: { clientName: 'gatewayGql' },
  });

  const [selectedValue, setValue] = React.useState('');

  const selectDropdownItem = async (link: string) => {
    await toggleCalendarEnabled({
      variables: {
        body: {
          userId: user.get().id.toString(),
          shouldDisplayContent: true,
        },
      },
    });
    window.open(link, '_blank');
  };

  if (!calendar) return null;

  return (
    <SelectField
      aria-label={_t('Select calendar type')}
      data-testid="calendar-sync-dropdown"
      css={css`
        label {
          display: none;
        }

        div {
          margin-top: 0 !important;
        }
      `}
      value={selectedValue}
      placeholder="Add to calendar"
      onChange={(event) => {
        const value = event.target.value as CalendarOptionValuesType;
        const link = getCalendarOptions(value, calendar).link;
        setValue(value);
        selectDropdownItem(link);
      }}
    >
      {calendarOptions.map((option: CalendarOptionValuesType) => (
        <SelectOption key={option} value={option} data-testid="calendar-option">
          {getCalendarOptions(option, calendar).label}
        </SelectOption>
      ))}
    </SelectField>
  );
};
export default compose<Props, {}>(withCalendarData)(CalendarSyncDropdown);
